import type { JSONSchema7 } from "json-schema";

/**
 * Filters a JSON schema to only include properties that exist in the provided data object.
 * Also preserves required definitions that are referenced by the filtered properties.
 *
 * @param data - The data object to check for existing properties
 * @param schema - The JSON schema to filter
 * @returns A filtered schema containing only properties present in the data object
 *
 * @example
 * const incident = { Narratives: [...], Officers: [...] };
 * const filtered = filterSchemaByData(incident, fullSchema);
 * // Returns schema with only Narratives and Officers properties
 */
export function filterSchemaByData(
  data: Record<string, any>,
  schema: JSONSchema7
): JSONSchema7 {
  // Get all properties that exist in the data object
  const existingProperties = Object.keys(data).filter(
    (key) => data[key] !== undefined && data[key] !== null
  );

  // Filter schema properties to only include those in the data
  const filteredProperties: Record<string, JSONSchema7> = {};
  
  if (schema.properties) {
    existingProperties.forEach((prop) => {
      if (schema.properties && prop in schema.properties) {
        filteredProperties[prop] = schema.properties[prop] as JSONSchema7;
      }
    });
  }

  // Collect all referenced definitions
  const referencedDefinitions = new Set<string>();
  const collectReferences = (obj: any): void => {
    if (!obj || typeof obj !== "object") return;

    if (typeof obj.$ref === "string") {
      const refName = obj.$ref.split("/").pop();
      if (refName) {
        referencedDefinitions.add(refName);
      }
    }

    if (Array.isArray(obj)) {
      obj.forEach(collectReferences);
    } else {
      Object.values(obj).forEach(collectReferences);
    }
  };

  // Collect references from filtered properties
  Object.values(filteredProperties).forEach(collectReferences);

  // Build filtered definitions with recursive reference collection
  const filteredDefinitions: Record<string, JSONSchema7> = {};
  const processedDefs = new Set<string>();

  const processDefinition = (defName: string): void => {
    if (processedDefs.has(defName) || !schema.definitions || !(defName in schema.definitions)) {
      return;
    }

    processedDefs.add(defName);
    const definition = schema.definitions[defName] as JSONSchema7;
    filteredDefinitions[defName] = definition;

    // Recursively collect referenced definitions
    collectReferences(definition);
  };

  // Process all referenced definitions
  referencedDefinitions.forEach(processDefinition);

  // Return filtered schema
  return {
    ...schema,
    properties: filteredProperties,
    ...(Object.keys(filteredDefinitions).length > 0 && {
      definitions: filteredDefinitions,
    }),
  };
}
